"use client";

import { cn } from "@woonstad/helpers-utils";
import clsx from "clsx";
import { format } from "date-fns";
import React, { useState } from "react";
import type { Matcher } from "react-day-picker";
import { isMobile } from "react-device-detect";

import { BasicAgenda } from "../../../icons/basic";
import { Calendar } from "../../calendar/Calendar";
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "../../popover/PopoverPrimitives";
import { Typography } from "../../typography/Typography";
import { useFormField } from "../FormField";
import { sharedInputClasses } from "../shared-input";
import { Text } from "./Text";

type Props = {
  /**
   * Placeholder text to show when the input is empty. As well as used for the aria-label attribute.
   */
  placeholder: string;
  /**
   * A function that takes a date and returns true if the date should be disabled.
   */
  calendarDisabledMatcher?: Matcher | Matcher[];
  /**
   * Whether to show dropdowns for year and month selection.
   */
  hasDateDropdowns?: boolean;
  hasNavigation?: boolean;
  fromYear?: number;
  toYear?: number;
  min?: string;
  widthClass?: string;
};

export const DatePicker = ({
  calendarDisabledMatcher,
  placeholder,
  hasDateDropdowns,
  hasNavigation = true,
  fromYear,
  toYear,
  min,
  widthClass,
  ...restProps
}: Props) => {
  const { field, fieldState, name, setValue } = useFormField();

  const date = field?.value ? new Date(field.value) : undefined;

  const [popoverOpen, setPopoverOpen] = useState(false);

  const inputClasses = clsx(
    sharedInputClasses,
    widthClass,
    "text-label-regular flex flex-wrap items-center gap-3 text-left",
    {
      "text-input-edited bg-input-enabled": !field?.disabled,
    },
    {
      "bg-input-disabled border-none text-input-disabled": field?.disabled,
    },
    {
      "border-input-error": fieldState.error,
    },
  );

  const setDate = (newValue?: Date) => {
    // Check if we receive a valid Date value
    if (
      !(newValue instanceof Date) ||
      !newValue ||
      isNaN(newValue as unknown as number)
    )
      return;
    setValue(name, newValue.toISOString(), {
      shouldValidate: true,
      shouldDirty: true,
    });

    setPopoverOpen(false);
  };

  if (!isMobile) {
    return (
      <PopoverRoot onOpenChange={setPopoverOpen} open={popoverOpen}>
        <PopoverTrigger asChild>
          <button
            aria-label={placeholder}
            className={inputClasses}
            disabled={field?.disabled}
            {...restProps}
          >
            {field?.value ? (
              <Typography variant="label-regular">
                {format(field.value, "dd-MM-yyyy")}
              </Typography>
            ) : (
              <Typography variant="label-regular">{placeholder}</Typography>
            )}
            <div
              className={clsx(
                "ml-auto size-5",
                field?.disabled && "text-icon-disabled",
              )}
            >
              <BasicAgenda />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={"w-auto border-0 bg-datepicker-default p-4 !shadow-large"}
          onInteractOutside={() => setPopoverOpen(false)}
          noMaxHeight
        >
          <Calendar
            hasDateDropdowns={hasDateDropdowns}
            hasNavigation={hasNavigation}
            autoFocus
            disabled={calendarDisabledMatcher}
            mode="single"
            selected={date}
            onSelect={(newDate) => setDate(newDate)}
            startMonth={new Date(fromYear ?? new Date().getFullYear(), 0)}
            endMonth={new Date(toYear ?? new Date().getFullYear(), 11)}
          />
        </PopoverContent>
      </PopoverRoot>
    );
  }

  return (
    <Text
      type="date"
      transformValue={(value) =>
        value ? format(new Date(value), "yyyy-MM-dd") : ""
      }
      className={cn(inputClasses, "date-input")}
      min={min}
      placeholder={placeholder}
      onChange={(ev) => {
        setDate(new Date(ev.currentTarget.value));
      }}
    />
  );
};

DatePicker.displayName = "DatePicker";
